/**
 * <MoneyTotal /> — aggregate of values across potentially-different
 * currencies, all converted to the profile currency. When ALL the items
 * share the same currency the rendering matches a plain `formatMoney(total)`.
 * When at least one item is in a foreign currency we prefix `≈` (same FX
 * indicator used by <MoneyAmount>) and expose a small "i" hover with the
 * per-currency breakdown so the user can see what was converted.
 *
 * Props:
 *   items     : [{ amount, currency }]  — list of monetary fragments
 *   currency  : string                  — target / profile currency
 *   sign      : "+"|"-"|null
 *   tone      : "income" | "expense" | "default"
 *   size      : "sm" | "md" | "lg"
 *   testid    : string
 *
 * Uses `useFxConvert` from queries.js so it respects the same FX rates
 * the rest of the app uses (server-cached 24h).
 */
import { formatMoney } from "../lib/api";
import { useFxConvert } from "../hooks/queries";
import { useLang } from "../contexts/LangContext";
import { Info } from "lucide-react";
import { tx } from "../i18n";

const SIZE_CLASSES = {
  sm: "text-sm",
  md: "text-base sm:text-lg",
  lg: "font-display text-2xl sm:text-3xl",
};

const TONE_CLASSES = {
  income: "text-emerald-700",
  expense: "text-red-600",
  default: "text-slate-900",
};

export function MoneyTotal({
  items = [],
  currency,
  sign = null,
  tone = "default",
  size = "md",
  testid = "money-total",
  className = "",
}) {
  const { lang } = useLang();
  const convert = useFxConvert();

  // Bucket by source currency so we can both compute the converted total
  // AND show the breakdown tooltip.
  const buckets = {};
  for (const it of items) {
    const cur = (it.currency || currency || "USD").toUpperCase();
    buckets[cur] = (buckets[cur] || 0) + Number(it.amount || 0);
  }
  const sources = Object.keys(buckets);
  const isMulti = sources.length > 1
    || (sources.length === 1 && sources[0] !== currency);

  // Convert each bucket to the profile currency, then sum.
  let total = 0;
  for (const src of sources) {
    total += convert(buckets[src], src, currency);
  }

  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.default;

  const tooltip = isMulti
    ? sources
        .map(
          (src) =>
            `${formatMoney(buckets[src], src)} ${src}` +
            (src !== currency ? ` → ${formatMoney(convert(buckets[src], src, currency), currency)}` : ""),
        )
        .join("\n")
    : null;

  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-1.5 ${className}`}
    >
      <div
        className={`font-bold tabular-nums flex items-center gap-1 ${sizeCls} ${toneCls}`}
        data-testid={`${testid}-value`}
      >
        {sign && <span>{sign}</span>}
        {isMulti && (
          <span
            className="text-slate-400 font-medium"
            title={tx(lang, `Approximate value in ${currency}. FX rate may have shifted.`, `Valor aproximado en ${currency}. La tasa FX puede haber cambiado.`, `Valor aproximado en ${currency}. La tasa FX puede haber cambiado.`)}
          >
            ≈
          </span>
        )}
        {formatMoney(total, currency)}
      </div>
      {isMulti && (
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          data-testid={`${testid}-breakdown`}
          aria-label={tx(lang, "View breakdown by currency", "Ver desglose por moneda", "Ver desglose por moneda")}
          title={tooltip}
          className="text-slate-400 hover:text-slate-700 transition-colors cursor-help"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
